if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.product-carousel__track');
      this.items = Array.from(this.querySelectorAll('.product-carousel__item'));
      this.prevBtn = this.querySelector('.product-carousel__arrow--prev');
      this.nextBtn = this.querySelector('.product-carousel__arrow--next');

      if (!this.track || this.items.length === 0) return;

      this.transitionSpeed = parseInt(this.dataset.transitionSpeed, 10) || 500;
      this.autoplay = this.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseFloat(this.dataset.autoplaySpeed) || 5) * 1000;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.idleTimer = null;
      this.animationId = 0;
      this.animating = false;
      this.infinite = false;

      if (this.dataset.infinite === 'true') this.setupInfinite();

      this.bindEvents();
      this.updateArrowState();

      if (this.autoplay) this.startAutoplay();
    }

    bindEvents() {
      this.prevBtn?.addEventListener('click', () => {
        this.scrollByStep(-1);
        this.pauseAutoplayTemporarily();
      });

      this.nextBtn?.addEventListener('click', () => {
        this.scrollByStep(1);
        this.pauseAutoplayTemporarily();
      });

      this.track.addEventListener('scroll', () => {
        this.updateArrowState();
        if (!this.infinite) return;
        clearTimeout(this.idleTimer);
        this.idleTimer = setTimeout(() => {
          if (!this.animating) this.normalizePosition();
        }, 150);
      }, { passive: true });
      this.track.addEventListener('pointerdown', () => this.pauseAutoplayTemporarily());

      this.addEventListener('mouseenter', () => this.stopAutoplay());
      this.addEventListener('mouseleave', () => {
        if (this.autoplay) this.startAutoplay();
      });

      window.addEventListener('resize', () => {
        if (this.infinite) this.measureLoop();
        this.updateArrowState();
      });
    }

    /* Infinite loop: [clones][originals][clones]. The track rests on the originals and,
       whenever it stops inside a clone set, jumps by one set width to the identical spot. */
    setupInfinite() {
      const count = this.items.length;
      const perView = Math.round(this.track.clientWidth / this.items[0].getBoundingClientRect().width) || 1;
      if (count <= perView) return;

      const makeClones = () =>
        this.items.map((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          clone.setAttribute('tabindex', '-1');
          clone.removeAttribute('aria-label');
          clone.querySelectorAll('img').forEach((img) => img.setAttribute('loading', 'lazy'));
          return clone;
        });

      this.track.prepend(...makeClones());
      this.track.append(...makeClones());
      this.infinite = true;
      this.measureLoop();
      this.jumpTo(this.setWidth);
    }

    measureLoop() {
      this.setWidth = this.items[0].offsetLeft - this.track.children[0].offsetLeft;
      if (!this.setWidth) return;
      this.jumpTo(this.setWidth + (this.track.scrollLeft % this.setWidth));
    }

    jumpTo(position) {
      const { style } = this.track;
      style.scrollBehavior = 'auto';
      style.scrollSnapType = 'none';
      this.track.scrollLeft = position;
      // eslint-disable-next-line no-unused-expressions
      this.track.offsetWidth;
      style.scrollBehavior = '';
      style.scrollSnapType = '';
    }

    normalizePosition() {
      const left = this.track.scrollLeft;
      if (left < this.setWidth - 1) this.jumpTo(left + this.setWidth);
      else if (left >= this.setWidth * 2 - 1) this.jumpTo(left - this.setWidth);
    }

    getStep() {
      const firstItem = this.items[0];
      const trackStyle = getComputedStyle(this.track);
      const gap = parseFloat(trackStyle.columnGap || trackStyle.gap) || 0;
      return firstItem.getBoundingClientRect().width + gap;
    }

    getMaxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    scrollByStep(direction) {
      const step = this.getStep();
      const maxScroll = this.getMaxScroll();
      let target = this.track.scrollLeft + step * direction;

      target = Math.max(0, Math.min(target, maxScroll));

      this.animateScrollTo(target);
    }

    animateScrollTo(target) {
      const start = this.track.scrollLeft;
      const distance = target - start;
      if (distance === 0) return;

      const duration = this.transitionSpeed;
      const startTime = performance.now();
      const easeInOutQuad = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);
      const id = ++this.animationId;
      this.animating = true;

      const step = (now) => {
        if (id !== this.animationId) return;
        const elapsed = now - startTime;
        const progress = Math.min(elapsed / duration, 1);
        this.track.scrollLeft = start + distance * easeInOutQuad(progress);
        if (progress < 1) {
          requestAnimationFrame(step);
        } else {
          this.animating = false;
          if (this.infinite) this.normalizePosition();
        }
      };

      requestAnimationFrame(step);
    }

    updateArrowState() {
      const maxScroll = this.getMaxScroll() - 1;
      const atStart = this.track.scrollLeft <= 0;
      const atEnd = this.track.scrollLeft >= maxScroll;

      this.prevBtn?.toggleAttribute('disabled', !this.infinite && atStart);
      this.nextBtn?.toggleAttribute('disabled', !this.infinite && atEnd);
    }

    startAutoplay() {
      if (!this.autoplay) return;
      this.stopAutoplay();
      this.autoplayTimer = setInterval(() => {
        const maxScroll = this.getMaxScroll() - 1;
        if (!this.infinite && this.track.scrollLeft >= maxScroll) {
          this.animateScrollTo(0);
        } else {
          this.scrollByStep(1);
        }
      }, this.autoplaySpeed);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    pauseAutoplayTemporarily() {
      if (!this.autoplay) return;
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startAutoplay(), 4000);
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
